import { SITE, absoluteUrl, pageTitle } from '@/shared/config/site';
import type { SeoInput } from './types';

/**
 * Marks head elements this module created, so they can be removed again when a
 * later page doesn't need them (`article:published_time` after navigating from
 * an article back to the home page). The value is the element's identity key.
 *
 * Tags that already exist in `index.html` — or that the edge Function injected
 * for a crawler — are *updated in place* and never carry this attribute, so
 * they're never removed and never duplicated.
 */
const MANAGED = 'data-seo';
const MANAGED_JSONLD = 'data-seo-jsonld';

/**
 * Robots directive for indexable pages. `max-image-preview:large` is what makes
 * a page eligible for large thumbnails in Google Discover and image-rich
 * results; the `max-snippet`/`max-video-preview` opt-outs of -1 remove the
 * default length caps.
 */
const INDEXABLE = 'index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1';
const BLOCKED = 'noindex, nofollow';

/**
 * Writes the document head for a page: title, description, canonical, robots,
 * Open Graph, Twitter cards and JSON-LD.
 *
 * Plain DOM rather than a head-manager dependency — the app is a single-page
 * client render, so there is exactly one head to own and no server pass to
 * reconcile with. Returns nothing; call it again to replace the previous page's
 * tags.
 */
export function applySeo(input: SeoInput, doc: Document = document): void {
  const head = doc.head;
  const seen = new Set<string>();

  const title = input.fullTitle?.trim() || pageTitle(input.title);
  const description = (input.description || SITE.description).trim();
  const canonical = absoluteUrl(input.path);
  const image = absoluteUrl(input.image || SITE.ogImage);
  const imageAlt = input.imageAlt || SITE.ogImageAlt;
  const type = input.type ?? 'website';

  doc.title = title;

  const meta = (attr: 'name' | 'property', key: string, content: string | null | undefined) => {
    if (!content) return;
    const id = `${attr}:${key}`;
    seen.add(id);
    let el = head.querySelector(`meta[${attr}="${escapeAttr(key)}"]`);
    if (!el) {
      el = doc.createElement('meta');
      el.setAttribute(attr, key);
      el.setAttribute(MANAGED, id);
      head.appendChild(el);
    }
    el.setAttribute('content', content);
  };

  const link = (rel: string, href: string) => {
    const id = `link:${rel}`;
    seen.add(id);
    let el = head.querySelector(`link[rel="${escapeAttr(rel)}"]`);
    if (!el) {
      el = doc.createElement('link');
      el.setAttribute('rel', rel);
      el.setAttribute(MANAGED, id);
      head.appendChild(el);
    }
    el.setAttribute('href', href);
  };

  meta('name', 'description', description);
  meta('name', 'robots', input.noIndex ? BLOCKED : INDEXABLE);
  link('canonical', canonical);

  meta('property', 'og:site_name', SITE.name);
  meta('property', 'og:type', type);
  meta('property', 'og:title', title);
  meta('property', 'og:description', description);
  meta('property', 'og:url', canonical);
  meta('property', 'og:image', image);
  meta('property', 'og:image:alt', imageAlt);
  meta('property', 'og:locale', SITE.locale);

  meta('name', 'twitter:card', 'summary_large_image');
  meta('name', 'twitter:title', title);
  meta('name', 'twitter:description', description);
  meta('name', 'twitter:image', image);
  meta('name', 'twitter:image:alt', imageAlt);
  if (SITE.twitterHandle) meta('name', 'twitter:site', SITE.twitterHandle);

  if (type === 'article' && input.article) {
    meta('property', 'article:published_time', input.article.publishedTime);
    meta('property', 'article:modified_time', input.article.modifiedTime);
    meta('property', 'article:author', input.article.author);
    meta('property', 'article:section', input.article.section);
  }

  // Retire tags we own that this page didn't ask for.
  head.querySelectorAll(`[${MANAGED}]`).forEach((el) => {
    if (!seen.has(el.getAttribute(MANAGED) ?? '')) el.remove();
  });

  // Structured data is replaced wholesale — the graphs are small, and diffing
  // them would cost more than rebuilding them.
  head.querySelectorAll(`script[${MANAGED_JSONLD}]`).forEach((el) => el.remove());
  for (const node of input.jsonLd ?? []) {
    const script = doc.createElement('script');
    script.setAttribute('type', 'application/ld+json');
    script.setAttribute(MANAGED_JSONLD, '');
    // `textContent`, never innerHTML: JSON.stringify escapes quotes but not the
    // `</script>` sequence that would otherwise break out of the element.
    script.textContent = JSON.stringify(node);
    head.appendChild(script);
  }
}

/**
 * Minimal attribute-value escaping for the selectors above. Meta keys are all
 * literals from this codebase, but quoting them defensively costs nothing.
 */
function escapeAttr(value: string): string {
  return value.replace(/["\\]/g, '\\$&');
}
